<template>
  <sidebars-layout
    :showLeftBar="false"
    mainContentClass="content-one-bar pl-4 pr-4"
  >
    <div id="portfolio-manager">
      <div class="header">
        <h3 class="module-desc">
          {{ $t("portfolioManager.moduleDesc") }}
        </h3>
        <div class="analyze-container" v-if="portfolio">
          <b-button
            variant="primary"
            v-if="canRebalance"
            @click="handleRebalancePortfolio"
          >
            <check-icon />
            {{ $t("portfolioManager.applyChangesShort") }}
          </b-button>
          <b-button
            variant="primary"
            v-if="canSavePortfolio"
            @click="handleSaveModal(PORTFOLIO)"
          >
            <save-icon />
            {{ $t("portfolioManager.table.saveAsPortfolio") }}
          </b-button>
          <b-button
            class="rerun-btn"
            :disabled="!canRerun"
            @click="handleOptimizePortfolio"
          >
            <b-spinner small v-if="isOptimizingPortfolio" />
            <replay-icon v-else-if="isCalculated" />
            <analyze-icon v-else />
            {{ isCalculated ? $t("portfolioManager.reRun") : "Analyze" }}
          </b-button>
          <tooltip-info
            :title="$t('portfolioManager.poolSidebar.applyChangesDisclaimer')"
            class="middle-tooltip"
            v-if="isCalculated"
          />
        </div>
      </div>
      <div class="top-desk" v-if="portfolio !== null">
        <div class="top-desk-header">
          <div class="portfolio-title" v-if="savedPortfolioItem">
            <h3>
              {{ savedPortfolioItem.portfolio_name }}
            </h3>
            <span class="info">{{
              $t("portfolioManager.currentPortfolio")
            }}</span>
          </div>
          <h3 v-else>{{ $t("portfolioManager.currentPortfolio") }}</h3>
          <div class="dots-dropdown">
            <b-dropdown variant="clear" no-caret>
              <template #button-content>
                <more-icon fillColor="#323232" />
              </template>
              <b-dropdown-item
                @click="handleSaveModal(PORTFOLIO)"
                v-if="canSavePortfolio"
              >
                <save-icon :fillColor="ICONS_FILL_COLOR" />
                {{ $t("portfolioManager.table.saveAsPortfolio") }}
              </b-dropdown-item>
              <b-dropdown-item @click="handleSaveModal(POOL)">
                <pool-icon :fillColor="ICONS_FILL_COLOR" />
                {{ $t("portfolioManager.table.saveAsPool") }}
              </b-dropdown-item>
              <b-dropdown-item
                @click="handleRebalancePortfolio"
                :disabled="!canRebalance"
              >
                <check-icon :fillColor="ICONS_FILL_COLOR" />
                {{ $t("portfolioManager.applyChanges") }}
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="($event) => handleShareModal()">
                <share-icon :fillColor="ICONS_FILL_COLOR" />
                {{ $t("portfolioManager.shareVersion") }}
              </b-dropdown-item>
              <b-dropdown-item
                @click="handleExportPortfolio"
                :disabled="!isSaved"
              >
                <export-icon
                  :fillColor="ICONS_FILL_COLOR"
                  :disabled="!isSaved"
                />
                {{ $t("portfolioManager.table.exportPortfolioExcel") }}
              </b-dropdown-item>
              <b-dropdown-item
                @click="handleExportPortfolioCsv"
                :disabled="!isSaved"
              >
                <export-csv-icon :fillColor="ICONS_FILL_COLOR" />
                {{ $t("portfolioManager.table.exportPortfolioCsv") }}
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="handleClearPortfolio">
                <span>
                  <upload-icon :fillColor="ICONS_FILL_COLOR" />
                  {{ $t("portfolioManager.table.uploadNewPortfolio") }}
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
        <div class="top-desk-body">
          <portfolio-table :portfolio="portfolio" />
          <div class="significant-measures-container">
            <h3>
              {{ $t("portfolioManager.significantMeasures.title") }}
              <b-spinner small v-if="isCalculatingMeasures" class="ml-1" />
            </h3>
            <div class="significant-measures" v-if="isCalculated">
              <significant-measure
                v-for="(measure, i) in [...significantMeasures, customMeasure]"
                v-bind="measure"
                :key="i"
                :value="
                  measure.value
                    ? measure.value
                    : portfolio.portfolio_output.custom_portfolio[measure.key]
                "
              />
            </div>
          </div>
          <weights-chart
            :portfolio="portfolio.portfolio_input.custom_portfolio"
          />
        </div>
      </div>
      <div v-else class="no-portfolio">
        <drop-zone
          @fileUploaded="handlePortfolioUpload"
          :isLoading="isOptimizingPortfolio"
          extensions="csv"
          :isPortfolioManager="true"
          id="pm"
        />
      </div>
      <markowitz-chart
        v-if="isCalculated || defaultPortfolio"
        @recalculateMeasures="handleRecalculateMeasures(true)"
      />
      <div
        class="lifecycle-chart-row"
        :class="{
          hasHistory: isLoadingHistoryVersion || selectedHistoryVersion,
        }"
      >
        <portfolio-lifecycle-chart v-if="portfolio && portfolio.portfolio_id" />
        <selected-history-version
          :isLoading="isLoadingHistoryVersion"
          :version="selectedHistoryVersion"
          v-if="selectedHistoryVersion && portfolio && portfolio.portfolio_id"
        />
      </div>
      <portfolio-file-explainer v-if="!portfolio" />
      <expanded-chart-modal />
      <save-portfolio-modal @saveItem="handleSaveItem" />
      <share-modal
        :portfolioName="
          portfolio &&
          savedPortfolioItem &&
          savedPortfolioItem.portfolio_name != ''
            ? savedPortfolioItem.portfolio_name
            : 'Portfolio'
        "
        :versionId="
          portfolio && savedPortfolioItem
            ? savedPortfolioItem.last_history_obj.history_id
            : null
        "
      />
    </div>
    <template v-slot:right>
      <pool-sidebar />
    </template>
  </sidebars-layout>
</template>

<script>
import {
  CSV_FILE,
  DARK_GREEN,
  DARK_RED,
  EXCEL_FILE,
  ICONS_FILL_COLOR,
  LIGHT_GREEN,
  LIGHT_PURPLE,
  LIGHT_RED,
  POOL,
  PORTFOLIO,
  PRIMARY_FONT_BLUE,
  RENAME_PORTFOLIO,
  RESERVE_TICKER,
} from "../utils/constants";
import {
  compareObjects,
  mapPortfolioToArray,
  reverseMapPortfolio,
} from "../utils/helper";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AnalyzeIcon from "vue-material-design-icons/ChartBoxOutline.vue";
import ChartIcon from "vue-material-design-icons/ChartLineStacked.vue";
import CheckIcon from "vue-material-design-icons/CheckCircleOutline.vue";
import CrossedDollar from "vue-material-design-icons/CurrencyUsdOff.vue";
import DollarIcon from "vue-material-design-icons/CurrencyUsd.vue";
import DropZone from "../components/DropZone.vue";
import ExpandedChartModal from "../components/modals/ExpandedChartModal.vue";
import ExportCsvIcon from "vue-material-design-icons/FileDelimitedOutline.vue";
import ExportIcon from "vue-material-design-icons/TableArrowDown.vue";
import MainAlert from "../components/alerts/MainAlert.vue";
import MarkowitzChart from "../components/charts/MarkowitzChart.vue";
import MoreIcon from "vue-material-design-icons/DotsVertical.vue";
import PoolIcon from "vue-material-design-icons/BookmarkOutline.vue";
import PoolSidebar from "../components/sidebars/PoolSidebar.vue";
import PortfolioFileExplainer from "../components/PortfolioFileExplainer.vue";
import PortfolioLifecycleChart from "../components/charts/PortfolioLifecycleChart.vue";
import PortfolioTable from "../components/tables/PortfolioTable.vue";
import ReplayIcon from "vue-material-design-icons/Replay.vue";
import RiskIcon from "vue-material-design-icons/Flash.vue";
import SaveIcon from "vue-material-design-icons/ContentSave.vue";
import SavePortfolioModal from "../components/modals/SaveModal.vue";
import SelectedHistoryVersion from "../components/SelectedHistoryVersion.vue";
import ShareIcon from "vue-material-design-icons/ShareVariant.vue";
import ShareModal from "../components/modals/ShareModal.vue";
import SidebarsLayout from "../layouts/SidebarsLayout.vue";
import SignificantMeasure from "../components/SignificantMeasure.vue";
import SumIcon from "vue-material-design-icons/Sigma.vue";
import TooltipInfo from "../components/TooltipInfo.vue";
import UploadIcon from "vue-material-design-icons/Upload.vue";
import WeightsChart from "../components/charts/WeightsChart.vue";
import moment from "moment";
export default {
  components: {
    AnalyzeIcon,
    SaveIcon,
    ReplayIcon,
    SidebarsLayout,
    DropZone,
    ExportCsvIcon,
    ExportIcon,
    SignificantMeasure,
    PoolSidebar,
    MarkowitzChart,
    PoolIcon,
    PortfolioTable,
    PortfolioFileExplainer,
    UploadIcon,
    WeightsChart,
    TooltipInfo,
    MoreIcon,
    ExpandedChartModal,
    SavePortfolioModal,
    PortfolioLifecycleChart,
    CheckIcon,
    SelectedHistoryVersion,
    ShareIcon,
    ShareModal,
  },
  data() {
    return {
      CSV_FILE,
      EXCEL_FILE,
      ICONS_FILL_COLOR,
      POOL,
      PORTFOLIO,
      savePortfolioModal: false,
      savePool: false,
      areSame: true,
      significantMeasures: [
        {
          title: this.$i18n.t("portfolioManager.sharpe"),
          tooltip: this.$i18n.t("portfolioManager.hints.sharpeRatio"),
          icon: ChartIcon,
          color: LIGHT_PURPLE,
          darkerColor: "#7266F3",
          key: "sharpe",
        },
        {
          title: this.$i18n.t(
            "portfolioManager.significantMeasures.expectedReturn"
          ),
          icon: DollarIcon,
          color: LIGHT_GREEN,
          darkerColor: DARK_GREEN,
          key: "expected_return",
          percentages: true,
          unit: true,
        },
        {
          title: this.$i18n.t(
            "portfolioManager.significantMeasures.volatility"
          ),
          icon: CrossedDollar,
          color: LIGHT_RED,
          darkerColor: DARK_RED,
          key: "volatility",
          percentages: true,
          unit: true,
        },
        {
          title: this.$i18n.t("portfolioManager.var"),
          tooltip: this.$i18n.t("portfolioManager.hints.var"),
          icon: RiskIcon,
          color: "#FFF7D6",
          darkerColor: "#FFD600",
          key: "valueAtRisk_5",
          percentages: true,
        },
      ],
    };
  },
  methods: {
    ...mapActions("portfolioManagerStore", [
      "optimizePortfolio",
      "importPortfolios",
      "exportPortfolio",
      "downloadPortfolioExcel",
      "savePortfolio",
      "getSavedPortfolios",
      "clearPortfolio",
      "createPool",
      "rebalancePortfolio",
      "getPortfolioHistories",
      "portfolioConversion",
      "getDefaultEfficientFrontier",
      "calculatePortfolioMetrics",
      "getAvailableTickers",
    ]),
    ...mapMutations("portfolioManagerStore", [
      "setIsOptimizingPortfolio",
      "analyzedPortfolio",
      "setCustomPortfolio",
      "setSaveModalItem",
      "setSelectedHistoryVersion",
      "setPortfolioHistories",
      "setIsWholeStocks",
      "setCash",
    ]),
    handleRebalancePortfolio() {
      this.rebalancePortfolio().then(() => {
        this.getPortfolioHistories(this.portfolio.portfolio_id);
        this.getSavedPortfolios();
        this.handleChangeInPortfolio(true);
        this.$toast.success({
          component: MainAlert,
          props: {
            message: this.$i18n.t(
              "portfolioManager.portfolioLifecycle.rebalanceSuccessful"
            ),
          },
        });
      });
    },
    handleOptimizePortfolio() {
      this.optimizePortfolio({
        history_id: this.portfolio.history_id
          ? this.portfolio.history_id
          : null,
        portfolio_id: this.portfolio.portfolio_id
          ? this.portfolio.portfolio_id
          : null,
        start: moment(this.dataSince).format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      }).catch((err) => {
        this.setIsOptimizingPortfolio(false);
        this.$toast.error({
          component: MainAlert,
          props: {
            message: err.response.data.detail,
          },
        });
      });
    },
    handlePortfolioUpload(file) {
      if (file.type === CSV_FILE) {
        this.setIsOptimizingPortfolio(true);
        let portfolio_input = {};

        this.$papa.parse(file, {
          delimiter: "",
          header: true,
          skipEmptyLines: true,
          complete: function (results) {
            let input = {};
            for (let i = 0; i < results.data.length; i++) {
              let insideObject = {};
              for (const [key, value] of Object.entries(results.data[i])) {
                if (key.toLowerCase() !== "ticker") {
                  let inside = {
                    [key.toLowerCase()]: Number(value.replace(/,/g, ".")),
                  };
                  insideObject = {
                    ...insideObject,
                    ...inside,
                  };
                } else {
                  results.data[i][key.toLowerCase()] = value;
                }
              }
              input[results.data[i].ticker] = insideObject;
            }
            const mappedInput = mapPortfolioToArray(input);
            portfolio_input = mappedInput;
          },
        });

        setTimeout(() => {
          const first = portfolio_input[0];
          const measuresTimeout = portfolio_input.length * 200;
          if (first.amount) {
            this.setIsWholeStocks(true);
          }
          if (first.capital || first.amount) {
            this.portfolioConversion({ portfolio: portfolio_input }).then(
              () => {
                setTimeout(
                  () => this.calculatePortfolioMetrics(),
                  measuresTimeout
                );
              }
            );
          } else {
            this.setCustomPortfolio(portfolio_input);
            setTimeout(() => this.calculatePortfolioMetrics(), measuresTimeout);
          }
          this.setIsOptimizingPortfolio(false);
        }, 1500);
      } else if (file.type === EXCEL_FILE) {
        this.setIsOptimizingPortfolio(true);
        this.importPortfolios({
          file: file,
          start: moment(this.dataSince).format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        })
          .then(() => {
            this.setIsOptimizingPortfolio(false);
            this.$toast.success({
              component: MainAlert,
              props: {
                message: this.$i18n.t(
                  "portfolioManager.dropzone.importSuccess"
                ),
              },
            });
          })
          .catch((err) => {
            this.setIsOptimizingPortfolio(false);
            this.$toast.error({
              component: MainAlert,
              props: {
                message:
                  err.response.status === 415
                    ? this.$i18n.t("errors.wrongFile")
                    : this.$i18n.t("errors.actionFailed"),
              },
            });
          });
      } else {
        this.setIsOptimizingPortfolio(false);
        this.$toast.error({
          component: MainAlert,
          props: {
            message: this.$i18n.t("errors.wrongFile"),
          },
        });
      }
    },
    handleClearPortfolio() {
      this.clearPortfolio(true);
      this.setSelectedHistoryVersion(null);
      this.setPortfolioHistories(null);
    },
    handleExportPortfolio() {
      const name = this.portfolio.name
        ? this.portfolio.name
        : this.portfolio.portfolio_name;
      this.downloadPortfolioExcel({
        portfolios: [Number(this.portfolio.history_id)],
        name: `${name}_${moment().format("DD.MM.YYYY")}`,
      });
    },
    handleExportPortfolioCsv() {
      this.exportPortfolio({
        custom_portfolio: this.portfolio.portfolio_input.custom_portfolio,
      })
        .then(() => {
          this.$toast.success({
            component: MainAlert,
            props: {
              message: this.$i18n.t("portfolioManager.table.portfolioExported"),
            },
          });
        })
        .catch(() => {
          this.$toast.error({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.actionFailed"),
            },
          });
        });
    },
    handleSavePortfolio(name) {
      const mergeData = {
        ...this.portfolio,
        ...this.recalculatedPortfolio
      };
      const params = {
        name,
        custom_portfolio_input: reverseMapPortfolio(
          mergeData.portfolio_input.custom_portfolio
        ),
        custom_portfolio_output:
          mergeData.portfolio_output.custom_portfolio,

        history_id: mergeData.history_id
          ? mergeData.history_id
          : this.defaultPortfolio.history_id,
      };

      this.savePortfolio(params)
        .then(() => {
          this.$toast.success({
            component: MainAlert,
            props: {
              message: this.$i18n.t(
                "portfolioManager.portfoliosSidebar.portfolioSaved"
              ),
            },
          });
        })
        .catch(() => {
          this.$toast.error({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.actionFailed"),
            },
          });
        });
    },
    handleSaveModal(item) {
      this.setSaveModalItem({ type: item });
      this.$bvModal.show("save-modal");
    },
    handleShareModal() {
      this.$bvModal.show("share-modal");
    },
    handleSaveItem(data) {
      if (data[POOL]) {
        this.handleSavePool(data[POOL]);
      }
      if (data[PORTFOLIO]) {
        this.handleSavePortfolio(data[PORTFOLIO]);
      }
      if (data[RENAME_PORTFOLIO]) {
        this.handleRenamePortfolio(data[RENAME_PORTFOLIO]);
      }
    },
    handleRenamePortfolio(name) {
      this.savePortfolio({ name: name, portfolio_id: this.saveModalItem.id })
        .then(() => {
          this.$toast.success({
            component: MainAlert,
            props: {
              message: this.$i18n.t(
                "portfolioManager.portfoliosSidebar.portfolioRenamed"
              ),
            },
          });
        })
        .catch(() => {
          this.$toast.error({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.actionFailed"),
            },
          });
        });
    },
    handleSavePool(name) {
      const pool = this.portfolio.portfolio_input.custom_portfolio.map(
        ({ ticker }) => ticker
      );
      const data = {
        name: name,
        description: "",
        tickers: pool,
      };
      this.createPool(data)
        .then(() => {
          this.$toast.success({
            component: MainAlert,
            props: {
              message: this.$i18n.t(
                "portfolioManager.portfoliosSidebar.poolSaved"
              ),
            },
          });
        })
        .catch(() => {
          this.$toast.success({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.actionFailed"),
            },
          });
        });
    },
    handleChangeInPortfolio(value) {
      this.areSame = value;
    },
    handleRecalculateMeasures(saveNewVersion) {
      this.calculatePortfolioMetrics({ saveNewVersion }).catch((err) => {
        this.$toast.error({
          component: MainAlert,
          props: {
            message:
              err.response.status !== 500
                ? err.response.data.detail
                : this.$i18n.t("errors.actionFailed"),
          },
        });
      });
    },
  },
  computed: {
    ...mapGetters("portfolioManagerStore", [
      "isOptimizingPortfolio",
      "portfolio",
      "dataSince",
      "savedPortfolios",
      "isWholeStocks",
      "cash",
      "selectedHistoryVersion",
      "isLoadingHistoryVersion",
      "replacePortfolioCoords",
      "recalculatedPortfolio",
      "saveModalItem",
      "defaultPortfolio",
      "tickersNonExistList",
      "existTickers",
      "isCalculatingFrontier",
      "isCalculatingSuggestion",
      "editMode",
      "isCalculatingMeasures",
    ]),
    canRerun() {
      return (
        this.tickersNonExistList.length === 0 &&
        !this.isOptimizingPortfolio &&
        !this.isCalculatingFrontier &&
        !this.isCalculatingSuggestion &&
        !this.editMode &&
        this.portfolio
      );
    },
    customMeasure() {
      const hasCapital =
        this.portfolio.portfolio_input.custom_portfolio[0].capital !== 0 &&
        this.cash !== null;
      const sumOfOpen = this.portfolio.portfolio_input.custom_portfolio
        .filter((item) => item.ticker !== RESERVE_TICKER)
        .reduce(
          (acc, curr) => acc + curr[hasCapital ? "capital" : "weight"],
          0
        );

      return {
        title: this.$i18n.t(
          "portfolioManager.significantMeasures.openPositionsValue"
        ),
        icon: SumIcon,
        color: "#F3F8FF",
        darkerColor: PRIMARY_FONT_BLUE,
        value: sumOfOpen,
        percentages: !this.isWholeStocks && !hasCapital,
        unit: false,
        dollars: hasCapital,
      };
    },
    isCalculated() {
      if (this.portfolio === null || !this.portfolio.portfolio_output) {
        return false;
      }
      return Object.keys(this.portfolio.portfolio_output).length !== 0;
    },
    savedPortfolioItem() {
      if (this.portfolio.portfolio_id) {
        return this.savedPortfolios.find(
          (item) => item.portfolio_id === this.portfolio.portfolio_id
        );
      }
      return null;
    },
    isSaved() {
      return this.savedPortfolioItem !== null;
    },
    canRebalance() {
      return (
        this.portfolio && this.savedPortfolioItem !== null && !this.areSame
      );
    },
    canSavePortfolio() {
      if (!this.portfolio) {
        return false;
      }
      if (!this.portfolio.portfolio_output) {
        return false;
      }
      return true;
    },
  },
  mounted() {
    this.getAvailableTickers();
    this.getDefaultEfficientFrontier();
  },
  watch: {
    portfolio: {
      handler(newVal, oldVal) {
        if (
          newVal === null ||
          (newVal !== null &&
            oldVal !== null &&
            newVal.portfolio_id !== oldVal.portfolio_id)
        ) {
          this.handleChangeInPortfolio(true);
        } else {
          if (
            this.savedPortfolioItem &&
            !compareObjects(
              mapPortfolioToArray(
                this.savedPortfolioItem.last_history_obj.custom_portfolio_input
              ),
              newVal.portfolio_input.custom_portfolio
            )
          ) {
            this.handleChangeInPortfolio(false);
          }
        }
      },
      deep: true,
    },
    isCalculated(newVal, oldVal) {
      if (newVal && !oldVal && this.portfolio.history_id) {
        this.analyzedPortfolio(this.portfolio.history_id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#portfolio-manager {
  padding-top: toRem(30);
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  .datepicker-root {
    display: flex;
    .middle-tooltip {
      margin-top: toRem(10);
      margin-left: toRem(5);
    }
  }
  .no-portfolio {
    width: 100%;
  }
  .top-desk {
    @include dps-box;
    position: relative;
    .top-desk-body {
      display: flex;
      column-gap: toRem(35);
      align-items: flex-start;
      flex-wrap: wrap;
    }
    .top-desk-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      .portfolio-title {
        margin-bottom: 0.5rem;
        .info {
          font-size: toRem(11);
        }
      }
    }
    .portfolio-table {
      flex-basis: 50%;
      min-width: 510px;
      max-width: 35%;
    }
    .significant-measures-container {
      flex-basis: 15%;
      h3 {
        font-size: toRem(14);
      }
    }
    .weights-chart-root {
      flex-basis: 35%;
      width: 35%;
    }
    .drop-zone {
      width: calc(100% - 25px);
    }

    @media screen and (max-width: $md) {
      flex-wrap: wrap;
      justify-content: space-around;
      .drop-zone,
      .portfolio-table {
        flex-basis: 100%;
      }
      .drop-zone {
        margin-bottom: 10px;
      }
    }
    .significant-measures-container {
      margin-top: toRem(15);
    }
    .weights-chart-root {
      margin-top: toRem(15);
      flex-basis: 30%;
    }
    .significant-measures {
      margin-top: toRem(25);
      display: flex;
      flex-wrap: wrap;
      column-gap: toRem(30);
      .significant-measure {
        margin-bottom: toRem(35);
        width: 100%;
      }
    }
  }
  .markowitz-chart-root {
    margin-top: toRem(50);
    @include dps-box;
  }
}
.analyze-container {
  display: flex;
  align-items: center;
  .btn {
    margin-right: toRem(5);
  }
}
.dots-dropdown {
  position: absolute;
  top: 15px;
  right: 4px;

  .dots-vertical-icon {
    cursor: pointer;
  }
}
</style>